import { defineStore, acceptHMRUpdate } from "pinia";
import { errorFromUnknown } from "@/errors";
import type { AppConfig } from "@/models/appConfig";
import { LocalStorage } from "quasar";
import { defaultAppConfig } from "@/parsers/appConfig";
import { Device } from "@capacitor/device";
import devConfig from "@/devConfig";

const loadingLock: {
    promise?: ReturnType<ReturnType<typeof useAppConfigStore>["loadFromLocal"]>;
} = {};

export const useAppConfigStore = defineStore("appConfig", {
    state: () => ({ ...defaultAppConfig(), loaded: false }),

    getters: {
        isTV: function (): boolean {
            return this.device.type === "tv";
        },
        isMobile: function (): boolean {
            return this.device.type === "mobile";
        },
        isDesktop: function (): boolean {
            return this.device.type === "desktop";
        }
    },

    actions: {
        async init(): Promise<ReturnType<typeof useAppConfigStore>> {
            if (this.loaded) return this;
            if (loadingLock.promise) {
                return await loadingLock.promise;
            }

            loadingLock.promise = this.loadFromLocal()
                .then(async () => {
                    await this.configureApi();
                    return this;
                })
                .catch(e => {
                    if (import.meta.env.QUASAR_DEV) {
                        const err = errorFromUnknown(e);
                        console.error(
                            "Error loading app config from local storage:",
                            err
                        );
                    }
                    return this;
                });

            return await loadingLock.promise;
        },
        async configureApi() {
            await this.$api.configure({
                baseURL: { default: this.apiBaseUrls.ott, ...this.apiBaseUrls },
                appInfo: {
                    device: { ...this.device },
                    domain: this.domain,
                    info: { ...this.info }
                },
                playerInfo: {
                    width: window.innerWidth,
                    height: window.innerWidth * (9 / 16)
                }
            });
            return this;
        },
        async loadDeviceInfo() {
            const [deviceInfo, deviceIdResult] = await Promise.all([
                Device.getInfo(),
                Device.getId()
            ]);
            this.device.id = deviceIdResult.identifier;
            this.device.model = deviceInfo.model;
            this.device.operatingSystem = deviceInfo.operatingSystem;
            this.device.osVersion = deviceInfo.osVersion;
            this.device.platform = deviceInfo.platform;
            this.device.manufacturer = deviceInfo.manufacturer;
            if (this.device.platform === "web") {
                this.features.recaptcha.enabled = true;
            }
            if (import.meta.env.QUASAR_CAPACITOR_MODE) {
                const [{ App }, ccp] = await Promise.all([
                    import("@capacitor/app"),
                    import("@/boot/capacitorCustomPlugins")
                ]);
                const [info, bundleIdResult] = await Promise.all([
                    App.getInfo(),
                    ccp.default.getMacroBundleId()
                ]);
                this.$state.info = {
                    appStore:
                        deviceInfo.platform === "web"
                            ? "web"
                            : deviceInfo.platform === "ios"
                              ? "apple"
                              : deviceInfo.manufacturer === "Amazon"
                                ? "amazon"
                                : "google",
                    storeUrl:
                        deviceInfo.manufacturer === "Amazon"
                            ? (import.meta.env.APP_AMAZON_STORE_URL ?? "")
                            : deviceInfo.platform === "android"
                              ? `https://play.google.com/store/apps/details?id=${info.id}`
                              : deviceInfo.platform === "ios"
                                ? (import.meta.env.APP_APPLE_STORE_URL ?? "")
                                : "",
                    bundleId: bundleIdResult.value,
                    name: info.name,
                    version: info.version,
                    build: info.build
                };
            } else {
                this.info = {
                    appStore: "web",
                    storeUrl: "",
                    bundleId: "video.gideo.webtv",
                    name: "Web TV",
                    version: "1.0.0",
                    build: "1"
                };
            }

            return this;
        },
        async loadFromLocal() {
            // Load from local storage if available for faster startup
            const localConfig = LocalStorage.getItem("appConfig");
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (window as unknown as { webtvConfig: any }).webtvConfig = {
                ...(typeof localConfig === "object" ? localConfig : {})
            };
            if (localConfig) {
                // We still need to load the config from the server to ensure it's up-to-date
                void this.load();
                this.$patch(localConfig as AppConfig);
            } else {
                return await this.load();
            }
            return this;
        },
        async load() {
            try {
                if (this.loaded) return this;
                const accountId: string =
                    this.accountId ||
                    devConfig?.accountId ||
                    import.meta.env.ACCOUNT_ID ||
                    "";
                const settingsUrl = accountId
                    ? `?cmd=getSettings&AccountID=${accountId}`
                    : `?cmd=getSettings&domain=${this.domain}`;
                await this.loadDeviceInfo();
                await this.configureApi();
                const config = (
                    await this.$api.get(settingsUrl, {
                        type: "AppConfig"
                    })
                ).data;
                const deviceType =
                    config.device.type === "unknown" ||
                    this.$deviceType === "tv"
                        ? this.$deviceType
                        : config.device.type;
                const mergedConfig: AppConfig = {
                    ...config,
                    device: { ...this.device, type: deviceType },
                    info: {
                        ...this.info,
                        storeUrl: config.info.storeUrl || this.info.storeUrl,
                        bundleId: config.info.bundleId || this.info.bundleId
                    }
                };

                this.$patch(mergedConfig);
                LocalStorage.remove("appConfig");
                LocalStorage.set("appConfig", mergedConfig);

                // We need to configure the API again after loading the config to ensure any changes from the server are applied
                await this.configureApi();
                this.loaded = true;
                // The video player needs access to the config on the window object, so we set it here after loading
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (window as unknown as { webtvConfig: any }).webtvConfig = {
                    ...mergedConfig
                };
            } catch (e) {
                if (import.meta.env.QUASAR_DEV) {
                    const err = errorFromUnknown(e);
                    console.error("Error loading app config:", err);
                }
            }

            return this;
        }
    }
});

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useAppConfigStore, import.meta.hot));
}
